import api from '../../../shared/services/apiClient';
import { DataSourceModel } from '../../../types';

export interface ColumnSchema {
  name: string;
  type: 'string' | 'number' | 'date' | 'boolean';
  role: 'dimension' | 'metric' | 'time_dimension';
  uniqueness: number;
  cardinality: number;
  nullPercentage: number;
  samples: string[];
}

export interface SmartSchema {
  tableName: string;
  columns: ColumnSchema[];
  primaryKeyCandidates: string[];
  metricCandidates: string[];
  dimensionCandidates: string[];
  timeDimensions: string[];
  suggestedCharts?: any[];
}

export interface JoinCandidate {
  left: string;
  right: string;
  confidence: number;
  reason: string;
}

export interface JoinInfo {
  joins: JoinCandidate[];
}

export interface EREntity {
  name: string;
  attributes: Array<{
    name: string;
    type: string;
    isPrimaryKey: boolean;
    isForeignKey: boolean;
    isNullable: boolean;
  }>;
}

export interface ERRelationship {
  from: string;
  to: string;
  cardinality: '1:1' | '1:N' | 'N:M';
  fromKey: string;
  toKey: string;
  relationshipType: 'identifies' | 'references' | 'associative';
}

export interface ERModel {
  entities: EREntity[];
  relationships: ERRelationship[];
  normalizedLevel: string;
  generatedAt: string;
  notes?: string;
}

export interface DataSourceSummary {
  id: number;
  name: string;
  description: string;
  type: string;
  records: number;
  fields: number;
  lastUpdate: string;
  status: string;
  hasSmartSchema?: boolean;
  hasERModel?: boolean;
}

/**
 * Get list of all datasources
 */
export const getDatasources = async (): Promise<DataSourceSummary[]> => {
  const response = await api.get('/datasources');
  return response.data;
};

/**
 * Get a single datasource with its latest analysis and model
 */
export const getDatasource = async (datasourceId: string | number): Promise<any> => {
  const response = await api.get(`/datasources/${datasourceId}`);
  return response.data;
};

/**
 * Get Smart Schema for a datasource
 */
export const getSmartSchema = async (datasourceId: string | number): Promise<SmartSchema> => {
  // Get datasource with latest analysis
  const response = await api.get(`/datasources/${datasourceId}`);
  const dataSource = response.data;
  
  // Check if analysis exists
  if (!dataSource.analyses || dataSource.analyses.length === 0) {
    throw new Error('No analysis available for this datasource. Please analyze it first.');
  }
  
  // Return the latest analysis's smart schema
  const latestAnalysis = dataSource.analyses[0];
  return latestAnalysis.smartSchemaJson as SmartSchema;
};

/**
 * Get ER Model for a datasource
 */
export const getERModel = async (datasourceId: string | number): Promise<ERModel> => {
  // Get datasource with latest auto model
  const response = await api.get(`/datasources/${datasourceId}`);
  const dataSource = response.data;
  
  // Check if auto model exists
  if (!dataSource.autoModels || dataSource.autoModels.length === 0) {
    throw new Error('No ER Model available for this datasource. Please analyze it first.');
  }
  
  // Return the latest auto model
  const latestModel = dataSource.autoModels[0];
  return latestModel.modelJson as ERModel;
};

/**
 * Generate ER Model from multiple schemas
 */
export const generateERModel = async (
  schemas: SmartSchema[],
  datasourceIds?: (string | number)[]
): Promise<ERModel> => {
  const response = await api.post('/model/er', { schemas, datasourceIds });
  return response.data.erModel;
};

/**
 * Generate Auto-Model (Modelo Relacional y Semántico completo)
 */
export const generateAutoModel = async (
  schemas: SmartSchema[],
  datasourceIds?: (string | number)[],
  businessDomain?: string
): Promise<any> => {
  const response = await api.post('/model/auto', { 
    schemas, 
    datasourceIds,
    businessDomain: businessDomain || 'agro'
  });
  return response.data.autoModel;
};

/**
 * Get Auto-Model for a datasource
 */
export const getAutoModel = async (datasourceId: string | number): Promise<any> => {
  const response = await api.get(`/model/auto/${datasourceId}`);
  return response.data.autoModel;
};

/**
 * Detect joins between multiple schemas
 */
export const detectJoins = async (schemas: SmartSchema[]): Promise<JoinInfo> => {
  // This would ideally be a backend endpoint, but we can compute client-side
  // For now, return empty - backend should expose this endpoint
  return { joins: [] };
};

/**
 * Get complete data model for a datasource (SmartSchema + AutoModel + StarSchema)
 * This is the new optimized endpoint for fast loading
 */
export const getDataSourceModel = async (datasourceId: string | number): Promise<DataSourceModel> => {
  const response = await api.get(`/datasources/${datasourceId}/model`);
  return response.data;
};
